import React from 'react';
import Plot from 'react-plotly.js';
import { Box, Typography } from '@mui/material';

const GWASForestPlot = ({ gwasData }) => {
  if (!gwasData || gwasData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No GWAS data available for this gene.
        </Typography>
      </Box>
    );
  }

  // Sort by Z-score (descending) for better visualization
  const sortedData = [...gwasData].sort((a, b) => {
    const zA = a.z_stat || 0;
    const zB = b.z_stat || 0;
    return zB - zA;
  });

  // Prepare data for plotting
  const disorders = sortedData.map(d => d.disorder);
  const zScores = sortedData.map(d => d.z_stat || 0);
  const ranks = sortedData.map(d => d.rank || null);
  
  // Create hover text with Z-score, P-value, and Rank
  const hoverText = sortedData.map(d => {
    const parts = [
      `Disorder: ${d.disorder}`,
      `Z-score: ${d.z_stat !== null && d.z_stat !== undefined ? d.z_stat.toFixed(3) : 'N/A'}`,
      `P-value: ${d.pval !== null && d.pval !== undefined ? d.pval.toExponential(2) : 'N/A'}`,
    ];
    if (d.rank !== null && d.rank !== undefined) {
      parts.push(`Rank: ${d.rank.toLocaleString()}`);
    }
    return parts.join('<br>');
  });

  // Determine colors based on Z-score significance
  const colors = zScores.map(z => {
    if (Math.abs(z) >= 5.45) return '#d32f2f'; // p < 5e-8 (genome-wide)
    if (Math.abs(z) >= 4.75) return '#f57c00'; // p < 1e-6 (highly significant)
    if (Math.abs(z) >= 1.96) return '#1976d2'; // p < 0.05 (significant)
    return '#757575'; // Not significant
  });

  const plotData = [
    {
      type: 'scatter',
      mode: 'markers',
      x: zScores,
      y: disorders,
      marker: {
        color: colors,
        size: 10,
        line: {
          color: '#000',
          width: 1
        }
      },
      text: hoverText,
      hoverinfo: 'text',
      orientation: 'h',
    }
  ];

  const layout = {
    title: {
      text: 'GWAS Results: Z-scores by Disorder',
      font: { size: 16 }
    },
    xaxis: {
      title: 'Z-score',
      zeroline: true,
      zerolinecolor: '#666',
      zerolinewidth: 2,
      gridcolor: '#e0e0e0'
    },
    yaxis: {
      title: '',
      automargin: true,
      gridcolor: '#e0e0e0'
    },
    height: Math.max(400, disorders.length * 40),
    margin: { l: 150, r: 50, t: 60, b: 50 },
    hovermode: 'closest',
    showlegend: false,
    shapes: [
      {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: 1.96,
        x1: 1.96,
        y0: 0,
        y1: 1,
        line: {
          color: '#1976d2',
          width: 1,
          dash: 'dash'
        }
      },
      {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: -1.96,
        x1: -1.96,
        y0: 0,
        y1: 1,
        line: {
          color: '#1976d2',
          width: 1,
          dash: 'dash'
        }
      },
      {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: 0,
        x1: 0,
        y0: 0,
        y1: 1,
        line: {
          color: '#666',
          width: 2
        }
      }
    ],
    annotations: [
      {
        x: 1.96,
        y: 1.02,
        xref: 'x',
        yref: 'paper',
        text: 'p = 0.05',
        showarrow: false,
        font: { size: 10, color: '#1976d2' }
      },
      {
        x: -1.96,
        y: 1.02,
        xref: 'x',
        yref: 'paper',
        text: 'p = 0.05',
        showarrow: false,
        font: { size: 10, color: '#1976d2' }
      }
    ]
  };

  // Create a second plot for rankings if available
  const hasRanks = ranks.some(r => r !== null && r !== undefined);
  let rankingPlotData = null;
  let rankingLayout = null;

  if (hasRanks) {
    // Sort by rank (ascending) for ranking plot
    const rankSortedData = [...gwasData]
      .filter(d => d.rank !== null && d.rank !== undefined)
      .sort((a, b) => a.rank - b.rank);

    const rankDisorders = rankSortedData.map(d => d.disorder);
    const rankValues = rankSortedData.map(d => d.rank);

    const rankHoverText = rankSortedData.map(d => {
      const parts = [
        `Disorder: ${d.disorder}`,
        `Rank: ${d.rank.toLocaleString()}`,
        `Z-score: ${d.z_stat !== null && d.z_stat !== undefined ? d.z_stat.toFixed(3) : 'N/A'}`,
        `P-value: ${d.pval !== null && d.pval !== undefined ? d.pval.toExponential(2) : 'N/A'}`,
      ];
      return parts.join('<br>');
    });

    // Color by rank (better rank = darker green)
    const rankColors = rankValues.map(rank => {
      if (rank <= 10) return '#2e7d32'; // Top 10 - dark green
      if (rank <= 100) return '#66bb6a'; // Top 100 - medium green
      if (rank <= 1000) return '#a5d6a7'; // Top 1000 - light green
      return '#c5e1a5'; // Others - very light green
    });

    rankingPlotData = [
      {
        type: 'scatter',
        mode: 'markers',
        x: rankValues,
        y: rankDisorders,
        marker: {
          color: rankColors,
          size: 10,
          line: {
            color: '#000',
            width: 1
          }
        },
        text: rankHoverText,
        hoverinfo: 'text',
        orientation: 'h',
      }
    ];

    rankingLayout = {
      title: {
        text: 'GWAS Results: Ranking by Disorder',
        font: { size: 16 }
      },
      xaxis: {
        title: 'Rank (lower is better)',
        type: 'log', // Use log scale for ranking
        gridcolor: '#e0e0e0'
      },
      yaxis: {
        title: '',
        automargin: true,
        gridcolor: '#e0e0e0'
      },
      height: Math.max(400, rankDisorders.length * 40),
      margin: { l: 150, r: 50, t: 60, b: 50 },
      hovermode: 'closest',
      showlegend: false
    };
  }

  return (
    <Box>
      <Plot
        data={plotData}
        layout={layout}
        config={{
          responsive: true,
          displayModeBar: true,
          modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        }}
        style={{ width: '100%' }}
      />
      
      {hasRanks && rankingPlotData && rankingLayout && (
        <Box sx={{ mt: 4 }}>
          <Plot
            data={rankingPlotData}
            layout={rankingLayout}
            config={{
              responsive: true,
              displayModeBar: true,
              modeBarButtonsToRemove: ['lasso2d', 'select2d'],
            }}
            style={{ width: '100%' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default GWASForestPlot;
